import { useState, useEffect } from 'react';
import { Zap, Check, RefreshCw, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { apiClient } from '../../lib/api-client';
import { cn } from '../../lib/utils';

interface WorkflowGroupPermission {
  access: 'unconditional' | 'conditional' | 'view' | 'none';
  approverPool?: 'unconditional' | 'conditional';
}

interface WorkflowPermissionConfig {
  displayName?: string;
  description?: string;
  groups: Record<string, WorkflowGroupPermission>;
}

interface WorkflowPermissionsData {
  workflows: Record<string, WorkflowPermissionConfig>;
  version: string;
  lastUpdated: string;
}

interface WorkflowPermissionsDialogProps {
  open: boolean;
  onClose: () => void;
  groupName: string;
  onSaved?: () => void;
}

const ACCESS_OPTIONS = [
  { value: 'none', label: 'No Access', description: 'Workflow is hidden for this group' },
  { value: 'view', label: 'View', description: 'Can see workflow, but cannot execute or request' },
  { value: 'conditional', label: 'Conditional', description: 'Can request execution (requires approval)' },
  { value: 'unconditional', label: 'Unconditional', description: 'Can execute immediately without approval' },
] as const;

const APPROVER_POOL_OPTIONS = [
  { value: 'unconditional', label: 'Unconditional only', description: 'Only users with unconditional access can approve' },
  { value: 'conditional', label: 'Conditional + Unconditional', description: 'Users with conditional or unconditional access can approve' },
] as const;

export function WorkflowPermissionsDialog({
  open,
  onClose,
  groupName,
  onSaved,
}: WorkflowPermissionsDialogProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [permissionsData, setPermissionsData] = useState<WorkflowPermissionsData | null>(null);
  const [localPermissions, setLocalPermissions] = useState<Record<string, WorkflowGroupPermission>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadPermissions();
    }
  }, [open, groupName]);

  const loadPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getWorkflowPermissions();
      setPermissionsData(data);

      // Extract current group's permissions from each workflow
      const groupPerms: Record<string, WorkflowGroupPermission> = {};
      for (const [workflowName, config] of Object.entries(data.workflows)) {
        const wfConfig = config as WorkflowPermissionConfig;
        if (wfConfig.groups[groupName]) {
          groupPerms[workflowName] = wfConfig.groups[groupName];
        } else {
          groupPerms[workflowName] = { access: 'none' };
        }
      }
      setLocalPermissions(groupPerms);
      setHasChanges(false);
    } catch (err: any) {
      console.error('Failed to load workflow permissions:', err);
      setError(err.message || 'Failed to load workflow permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await apiClient.syncWorkflowPermissions();
      setPermissionsData(result.permissions);

      // Re-extract group permissions
      const groupPerms: Record<string, WorkflowGroupPermission> = {};
      for (const [workflowName, config] of Object.entries(result.permissions.workflows)) {
        const wfConfig = config as WorkflowPermissionConfig;
        if (wfConfig.groups[groupName]) {
          groupPerms[workflowName] = wfConfig.groups[groupName];
        } else {
          groupPerms[workflowName] = { access: 'none' };
        }
      }
      setLocalPermissions(groupPerms);
    } catch (err: any) {
      console.error('Failed to sync workflow permissions:', err);
      setError(err.message || 'Failed to sync workflow permissions');
    } finally {
      setSyncing(false);
    }
  };

  const handleAccessChange = (workflowName: string, access: 'unconditional' | 'conditional' | 'view' | 'none') => {
    setLocalPermissions((prev) => ({
      ...prev,
      [workflowName]: {
        ...prev[workflowName],
        access,
        // Set default approverPool when switching to conditional
        approverPool: access === 'conditional' ? (prev[workflowName]?.approverPool || 'unconditional') : undefined,
      },
    }));
    setHasChanges(true);
  };

  const handleApproverPoolChange = (workflowName: string, approverPool: 'unconditional' | 'conditional') => {
    setLocalPermissions((prev) => ({
      ...prev,
      [workflowName]: {
        ...prev[workflowName],
        approverPool,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Save changes for each workflow that has been modified
      const savePromises: Promise<any>[] = [];

      for (const [workflowName, permission] of Object.entries(localPermissions)) {
        const originalPerm = permissionsData?.workflows[workflowName]?.groups[groupName];
        const hasChanged =
          !originalPerm ||
          originalPerm.access !== permission.access ||
          (permission.access === 'conditional' && originalPerm.approverPool !== permission.approverPool);

        if (hasChanged) {
          if (permission.access === 'none') {
            // Remove permission if set to none (unless it was already none)
            if (originalPerm) {
              savePromises.push(apiClient.removeWorkflowPermission(workflowName, groupName));
            }
          } else {
            // Update permission
            savePromises.push(apiClient.updateWorkflowPermission(workflowName, groupName, permission));
          }
        }
      }

      await Promise.all(savePromises);
      setHasChanges(false);
      onSaved?.();
      onClose();
    } catch (err: any) {
      console.error('Failed to save workflow permissions:', err);
      setError(err.message || 'Failed to save workflow permissions');
    } finally {
      setSaving(false);
    }
  };

  const workflowEntries = permissionsData
    ? Object.entries(permissionsData.workflows).sort(([, a], [, b]) =>
        (a.displayName || '').localeCompare(b.displayName || '')
      )
    : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent maxWidth="3xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Workflow Permissions: {groupName}
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={loadPermissions}>
                Retry
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Info banner */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Access Levels:</strong> <em>Unconditional</em> = execute immediately.{' '}
                  <em>Conditional</em> = requires approval from eligible approvers before execution.{' '}
                  Users with the <em>Workflow Administrator</em> flag have unconditional access to all workflows.
                </div>
              </div>

              {/* Sync button */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSync}
                  disabled={syncing}
                >
                  <RefreshCw className={cn('w-4 h-4 mr-1', syncing && 'animate-spin')} />
                  {syncing ? 'Syncing...' : 'Sync Workflows'}
                </Button>
              </div>

              {/* Workflow list */}
              {workflowEntries.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No workflows configured. Click "Sync Workflows" to discover workflows from definitions.
                </div>
              ) : (
                <div className="space-y-3">
                  {workflowEntries.map(([workflowName, config]) => {
                    const wfConfig = config as WorkflowPermissionConfig;
                    const currentPerm = localPermissions[workflowName] || { access: 'none' };

                    return (
                      <Card key={workflowName}>
                        <CardContent className="p-4">
                          <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                            {/* Workflow info */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {wfConfig.displayName || workflowName}
                              </h4>
                              {wfConfig.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  {wfConfig.description}
                                </p>
                              )}
                              <code className="text-xs text-gray-500 dark:text-gray-500 mt-1 block">
                                {workflowName}
                              </code>
                            </div>

                            {/* Access level selector */}
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-wrap gap-1">
                                {ACCESS_OPTIONS.map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleAccessChange(workflowName, option.value)}
                                    className={cn(
                                      'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                                      currentPerm.access === option.value
                                        ? option.value === 'unconditional'
                                          ? 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                                          : option.value === 'conditional'
                                          ? 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                                          : 'bg-gray-100 border-gray-300 text-gray-800 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300'
                                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800'
                                    )}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>

                              {/* Approver pool selector (only for conditional) */}
                              {currentPerm.access === 'conditional' && (
                                <div className="mt-2 pl-2 border-l-2 border-amber-300 dark:border-amber-700">
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                    Who can approve requests from this group:
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {APPROVER_POOL_OPTIONS.map((option) => (
                                      <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => handleApproverPoolChange(workflowName, option.value)}
                                        className={cn(
                                          'px-2 py-1 text-xs rounded border transition-colors',
                                          currentPerm.approverPool === option.value
                                            ? 'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800'
                                        )}
                                        title={option.description}
                                      >
                                        {option.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Version info */}
              {permissionsData && (
                <div className="text-xs text-gray-500 dark:text-gray-500 text-right">
                  Version: {permissionsData.version} | Last updated:{' '}
                  {new Date(permissionsData.lastUpdated).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
